import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Package } from "lucide-react";

import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import {
  CaixasFilterModal,
  EMPTY_CAIXA_FILTERS,
  type CaixaFilterDraft,
} from "@/components/caixas/CaixasFilterModal";
import { CaixasManualTransactionModal } from "@/components/caixas/CaixasManualTransactionModal";
import { CaixasPageHeader } from "@/components/caixas/CaixasPageHeader";
import { CAIXAS_STYLES } from "@/components/caixas/CaixasStyles";
import { CaixasSupplierSummaryList } from "@/components/caixas/CaixasSupplierSummaryList";
import { CaixasToolbar } from "@/components/caixas/CaixasToolbar";
import { CaixasTransactionLedger } from "@/components/caixas/CaixasTransactionLedger";
import { CaixasUndoConfirmDialog } from "@/components/caixas/CaixasUndoConfirmDialog";
import { useFloatingFeedbackToast } from "@/components/caixas/useFloatingFeedbackToast";
import { FloatingToast } from "@/components/feedback/FloatingToast";
import { OperationalPager } from "@/components/operational/OperationalPager";
import { OperationalQueryState } from "@/components/operational/OperationalQueryState";
import { OperationalSearchField } from "@/components/operational/OperationalSearchField";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { formatSignedCaixaBalance } from "@/features/caixas/display";
import {
  useCaixaSupplierSummaries,
  useCreateManualCaixaTransaction,
  useSupplierCaixaTransactions,
  useUndoCaixaTransaction,
} from "@/features/caixas/hooks";
import type {
  CaixaSupplierSummary,
  CaixaTransactionRow,
} from "@/features/caixas/types";
import { useMediaQuery } from "@/hooks/useMediaQuery";

import { SupplierCaixasPage } from "./SupplierCaixasPage";

const PAGE_SIZE = 20;

function balanceColor(value: number) {
  if (value > 0) return "#16a34a";
  if (value < 0) return "#ef4444";
  return "var(--login-muted)";
}

function DirectorySkeleton() {
  return (
    <div
      className="caixas-history-skeleton"
      data-testid="caixas-directory-skeleton"
    >
      <div className="caixas-skeleton-toolbar">
        <span className="caixas-skeleton-line" />
        <span className="caixas-skeleton-line" />
      </div>
      {Array.from({ length: 5 }, (_, row) => (
        <div className="caixas-skeleton-row" key={row}>
          {Array.from({ length: 4 }, (_, column) => (
            <span className="caixas-skeleton-line" key={column} />
          ))}
        </div>
      ))}
    </div>
  );
}

function GeneralizedCaixasPage() {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { toast, showToast } = useFloatingFeedbackToast();
  const [summaryDraftSearch, setSummaryDraftSearch] = useState("");
  const [summarySearch, setSummarySearch] = useState("");
  const [summaryPage, setSummaryPage] = useState(0);
  const [selectedSupplier, setSelectedSupplier] =
    useState<CaixaSupplierSummary | null>(null);
  const [draftSearch, setDraftSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [draftFilters, setDraftFilters] =
    useState<CaixaFilterDraft>(EMPTY_CAIXA_FILTERS);
  const [appliedFilters, setAppliedFilters] =
    useState<CaixaFilterDraft>(EMPTY_CAIXA_FILTERS);
  const [historyPage, setHistoryPage] = useState(0);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [pendingManualSupplier, setPendingManualSupplier] =
    useState<CaixaSupplierSummary | null>(null);
  const [pendingUndo, setPendingUndo] = useState<CaixaTransactionRow | null>(
    null,
  );
  const [dismissedSummaryError, setDismissedSummaryError] =
    useState<unknown>(null);
  const [dismissedHistoryError, setDismissedHistoryError] =
    useState<unknown>(null);

  useEffect(() => {
    const nextSearch = summaryDraftSearch.trim();
    if (nextSearch === summarySearch) return;
    const timeout = window.setTimeout(() => {
      setSummarySearch(nextSearch);
      setSummaryPage(0);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [summaryDraftSearch, summarySearch]);

  useEffect(() => {
    const nextSearch = draftSearch.trim();
    if (nextSearch === appliedSearch) return;
    const timeout = window.setTimeout(() => {
      setAppliedSearch(nextSearch);
      setHistoryPage(0);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [draftSearch, appliedSearch]);

  const summaryParams = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset: summaryPage * PAGE_SIZE,
      ...(summarySearch ? { search: summarySearch } : {}),
    }),
    [summaryPage, summarySearch],
  );
  const summariesQuery = useCaixaSupplierSummaries(summaryParams);
  const querySummaries = summariesQuery.data?.items ?? [];
  const summaryTotal = summariesQuery.data?.total ?? 0;
  const summaryLastPage = Math.max(0, Math.ceil(summaryTotal / PAGE_SIZE) - 1);
  const summaryNeedsPageClamp = Boolean(
    summariesQuery.data &&
    !summariesQuery.isPlaceholderData &&
    summaryPage > summaryLastPage,
  );
  const retainedSummariesRef = useRef({
    rows: querySummaries,
    total: summaryTotal,
  });
  if (querySummaries.length > 0) {
    retainedSummariesRef.current = {
      rows: querySummaries,
      total: summaryTotal,
    };
  }
  const displayedSummaries = summaryNeedsPageClamp
    ? retainedSummariesRef.current
    : { rows: querySummaries, total: summaryTotal };
  const summaries = displayedSummaries.rows;

  useEffect(() => {
    if (!summariesQuery.data || summariesQuery.isPlaceholderData) return;
    if (summaryPage > summaryLastPage) setSummaryPage(summaryLastPage);
  }, [
    summariesQuery.data,
    summariesQuery.isPlaceholderData,
    summaryLastPage,
    summaryPage,
  ]);

  useEffect(() => {
    if (!summariesQuery.error) setDismissedSummaryError(null);
  }, [summariesQuery.error]);

  useEffect(() => {
    if (!selectedSupplier) return;
    const refreshed = summaries.find((item) => item.id === selectedSupplier.id);
    if (refreshed) setSelectedSupplier(refreshed);
  }, [selectedSupplier?.id, summaries]);

  const historyParams = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset: historyPage * PAGE_SIZE,
      ...(appliedSearch ? { search: appliedSearch } : {}),
      ...(appliedFilters.client ? { client: appliedFilters.client } : {}),
      ...(appliedFilters.store ? { store: appliedFilters.store } : {}),
      ...(appliedFilters.from ? { from: appliedFilters.from } : {}),
      ...(appliedFilters.to ? { to: appliedFilters.to } : {}),
    }),
    [appliedFilters, appliedSearch, historyPage],
  );
  const historyQuery = useSupplierCaixaTransactions(
    selectedSupplier?.id ?? null,
    historyParams,
  );
  const historyTotal = historyQuery.data?.total ?? 0;
  const queryHistoryRows = historyQuery.data?.items ?? [];
  const historyLastPage = Math.max(0, Math.ceil(historyTotal / PAGE_SIZE) - 1);
  const historyNeedsPageClamp = Boolean(
    historyQuery.data &&
    !historyQuery.isPlaceholderData &&
    historyPage > historyLastPage,
  );
  const retainedHistoryRef = useRef({
    rows: queryHistoryRows,
    total: historyTotal,
  });
  if (queryHistoryRows.length > 0) {
    retainedHistoryRef.current = {
      rows: queryHistoryRows,
      total: historyTotal,
    };
  }
  const displayedHistory = historyNeedsPageClamp
    ? retainedHistoryRef.current
    : { rows: queryHistoryRows, total: historyTotal };

  useEffect(() => {
    if (!historyQuery.data || historyQuery.isPlaceholderData) return;
    if (historyPage > historyLastPage) setHistoryPage(historyLastPage);
  }, [
    historyLastPage,
    historyPage,
    historyQuery.data,
    historyQuery.isPlaceholderData,
  ]);

  useEffect(() => {
    if (!historyQuery.error) setDismissedHistoryError(null);
  }, [historyQuery.error]);

  const createManualMutation = useCreateManualCaixaTransaction();
  const undoMutation = useUndoCaixaTransaction();
  const hasFilters = Boolean(
    appliedFilters.client ||
    appliedFilters.store ||
    appliedFilters.from ||
    appliedFilters.to,
  );

  function clearSearch() {
    setDraftSearch("");
    setAppliedSearch("");
    setHistoryPage(0);
  }

  function applyFilters() {
    const normalizedFilters = {
      ...draftFilters,
      client: draftFilters.client.trim(),
      store: draftFilters.store.trim(),
    };
    setDraftFilters(normalizedFilters);
    setAppliedFilters(normalizedFilters);
    setHistoryPage(0);
    setIsFilterModalOpen(false);
  }

  function clearFilters() {
    setDraftFilters(EMPTY_CAIXA_FILTERS);
    setAppliedFilters(EMPTY_CAIXA_FILTERS);
    setHistoryPage(0);
  }

  function openCarga(checkinId: string) {
    navigate({ to: "/cargas/$checkinId", params: { checkinId } });
  }

  async function confirmUndo() {
    if (!pendingUndo) return;
    try {
      await undoMutation.mutateAsync(pendingUndo.id);
      setPendingUndo(null);
      showToast({
        tone: "success",
        title: "Movimentação desfeita",
        message: "O histórico foi atualizado com o desfazimento.",
      });
    } catch (error) {
      showToast({
        tone: "error",
        title: "Não foi possível desfazer",
        message: getUserFacingErrorMessage(
          error,
          "Não foi possível desfazer a movimentação. Tente novamente.",
        ),
      });
    }
  }

  return (
    <>
      <style>{CAIXAS_STYLES}</style>
      <div className="caixas-shell">
        <AdminBreadcrumb
          items={
            selectedSupplier
              ? [
                  {
                    label: "Caixas",
                    onClick: () => {
                      setSelectedSupplier(null);
                      setHistoryPage(0);
                    },
                  },
                  { label: selectedSupplier.name },
                ]
              : [
                  { label: "Mais", onClick: () => navigate({ to: "/mais" }) },
                  { label: "Caixas" },
                ]
          }
        />
        <CaixasPageHeader />

        {selectedSupplier ? (
          <main className="caixas-page-stage caixas-history" key="history">
            <div className="caixas-history-identity">
              <div className="caixas-history-copy">
                <span>Histórico de transações</span>
                <h2>{selectedSupplier.name}</h2>
              </div>
              <div className="caixas-balance-summary">
                <span className="caixas-balance-summary-label">
                  Saldo atual
                </span>
                <strong
                  className="caixas-balance-summary-value"
                  style={{ color: balanceColor(selectedSupplier.box_balance) }}
                >
                  {formatSignedCaixaBalance(selectedSupplier.box_balance)}
                </strong>
              </div>
              <button
                type="button"
                className="caixas-pill-button caixas-pill-button-primary"
                onClick={() => setPendingManualSupplier(selectedSupplier)}
              >
                <Package size={16} />
                Registrar movimentação
              </button>
            </div>
            <CaixasToolbar
              draftSearch={draftSearch}
              onDraftSearchChange={setDraftSearch}
              onClearSearch={clearSearch}
              onOpenFilters={() => {
                setDraftFilters(appliedFilters);
                setIsFilterModalOpen(true);
              }}
              hasAppliedFilters={hasFilters}
              onClearFilters={clearFilters}
            />
            <CaixasTransactionLedger
              rows={displayedHistory.rows}
              isLoading={historyQuery.isLoading}
              isFetching={historyQuery.isFetching || historyNeedsPageClamp}
              isFiltered={Boolean(appliedSearch || hasFilters)}
              error={displayedHistory.rows.length ? null : historyQuery.error}
              total={displayedHistory.total}
              page={historyPage}
              pageSize={PAGE_SIZE}
              onPageChange={setHistoryPage}
              onRetry={() => historyQuery.refetch()}
              onOpenCarga={openCarga}
              onUndo={setPendingUndo}
            />
          </main>
        ) : (
          <main className="caixas-page-stage" key="directory">
            <OperationalQueryState
              hasData={summaries.length > 0}
              isLoading={summariesQuery.isLoading}
              isFetching={summariesQuery.isFetching || summaryNeedsPageClamp}
              error={summaries.length ? null : summariesQuery.error}
              isFiltered={Boolean(summarySearch)}
              onRetry={() => summariesQuery.refetch()}
              renderSkeleton={() => <DirectorySkeleton />}
              renderEmpty={({ filtered }) => (
                <section className="caixas-empty-state">
                  <div>
                    <strong>Nenhum fornecedor encontrado</strong>
                    <p>
                      {filtered
                        ? "Ajuste ou limpe a pesquisa aplicada."
                        : "Não há fornecedores disponíveis."}
                    </p>
                    {filtered ? (
                      <button
                        type="button"
                        className="caixas-pill-button caixas-pill-button-secondary"
                        onClick={() => {
                          setSummaryDraftSearch("");
                          setSummarySearch("");
                          setSummaryPage(0);
                        }}
                      >
                        Limpar busca
                      </button>
                    ) : null}
                  </div>
                </section>
              )}
            >
              <section
                className="caixas-directory"
                aria-label="Fornecedores"
                aria-busy={summariesQuery.isFetching}
              >
                <div className="caixas-directory-toolbar">
                  <OperationalSearchField
                    value={summaryDraftSearch}
                    onChange={setSummaryDraftSearch}
                    placeholder="Pesquisar fornecedores"
                    ariaLabel="Pesquisar fornecedores"
                  />
                </div>
                <div className="caixas-supplier-head" aria-hidden="true">
                  <span>Fornecedor</span>
                  <span>Última movimentação</span>
                  <span>Saldo</span>
                  <span />
                </div>
                <CaixasSupplierSummaryList
                  suppliers={summaries}
                  onOpenHistory={(supplier) => {
                    setSelectedSupplier(supplier);
                    setHistoryPage(0);
                  }}
                />
                <OperationalPager
                  variant="numbered"
                  totalItems={displayedSummaries.total}
                  limit={PAGE_SIZE}
                  offset={summaryPage * PAGE_SIZE}
                  page={summaryPage}
                  pageCount={Math.max(
                    1,
                    Math.ceil(displayedSummaries.total / PAGE_SIZE),
                  )}
                  onPageChange={setSummaryPage}
                  ariaLabel="Paginação dos fornecedores"
                  itemLabel="fornecedor"
                  itemLabelPlural="fornecedores"
                  className="caixas-pager"
                />
              </section>
            </OperationalQueryState>
          </main>
        )}
      </div>

      {isFilterModalOpen ? (
        <CaixasFilterModal
          draft={draftFilters}
          onChange={setDraftFilters}
          onApply={applyFilters}
          onClear={clearFilters}
          onClose={() => setIsFilterModalOpen(false)}
        />
      ) : null}
      {pendingManualSupplier ? (
        <CaixasManualTransactionModal
          supplier={pendingManualSupplier}
          onClose={() => setPendingManualSupplier(null)}
          mutationOverride={{
            isPending: createManualMutation.isPending,
            mutateAsync: async (args) => {
              await createManualMutation.mutateAsync(args);
              showToast({
                tone: "success",
                title: "Movimentação registrada",
                message: "Saldo de caixas atualizado.",
              });
            },
          }}
        />
      ) : null}
      {pendingUndo ? (
        <CaixasUndoConfirmDialog
          transaction={pendingUndo}
          onCancel={() => setPendingUndo(null)}
          onConfirm={confirmUndo}
          isPending={undoMutation.isPending}
        />
      ) : null}
      {!selectedSupplier &&
      summaries.length > 0 &&
      summariesQuery.error &&
      dismissedSummaryError !== summariesQuery.error ? (
        <FloatingToast
          tone="error"
          title="Não foi possível atualizar os fornecedores"
          message={getUserFacingErrorMessage(
            summariesQuery.error,
            "Tente novamente em alguns instantes.",
          )}
          actionLabel="Tentar novamente"
          onAction={() => {
            setDismissedSummaryError(summariesQuery.error);
            void summariesQuery.refetch();
          }}
          onDismiss={() => setDismissedSummaryError(summariesQuery.error)}
          isDesktop={isDesktop}
        />
      ) : null}
      {selectedSupplier &&
      displayedHistory.rows.length > 0 &&
      historyQuery.error &&
      dismissedHistoryError !== historyQuery.error ? (
        <FloatingToast
          tone="error"
          title="Não foi possível atualizar o histórico"
          message={getUserFacingErrorMessage(
            historyQuery.error,
            "Tente novamente em alguns instantes.",
          )}
          actionLabel="Tentar novamente"
          onAction={() => {
            setDismissedHistoryError(historyQuery.error);
            void historyQuery.refetch();
          }}
          onDismiss={() => setDismissedHistoryError(historyQuery.error)}
          isDesktop={isDesktop}
        />
      ) : null}
      {toast ? (
        <FloatingToast
          title={toast.title}
          message={toast.message}
          tone={toast.tone}
          state={toast.state}
          isDesktop={isDesktop}
        />
      ) : null}
    </>
  );
}

export function CaixasPage() {
  const { data: currentUser } = useCurrentUser();
  if (currentUser?.role === "supplier") return <SupplierCaixasPage />;
  if (currentUser?.role !== "super" && currentUser?.role !== "checker") {
    return null;
  }
  return <GeneralizedCaixasPage />;
}
